// StreamChat.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useUser } from 'context/UserContext';

// Firebase
import { onValue, push, query, ref, limitToLast, orderByChild } from 'firebase/database';

// CSS
import './StreamChat.css';

// Components
import Button from 'components/button/Button';
import { realTimeDb } from 'firebaseConfig';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * StreamChat
 * 
 * Viewer-facing live chat component for LIVE_STREAM nodes.
 * 
 * @param {object} node - The LIVE_STREAM node object.
 * 
 * @returns {ReactNode} - StreamChat component.
 */
const StreamChat = ({ node }) => {

  // ----- CONTEXT ----- //

  /* ----- CONTEXT ----- */

  // User Context
  const { user } = useUser();

  // ----- STATE ----- //

  /* ----- STATE ----- */

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // ----- REFS ----- //

  const listRef = useRef(null);

  const nodeKey = node?.key || null;

  // ----- MEMOS ----- //

  const displayName = useMemo(() => {
    if (!user) return 'Guest';
    return (
      user?.data_profile?.name ||
      user?.name ||
      'Guest'
    );
  }, [user]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!nodeKey) return undefined;

    const chatRef = ref(realTimeDb, `streamChats/${nodeKey}`);
    const chatQuery = query(chatRef, orderByChild('dateCreated'), limitToLast(200));

    const unsubscribe = onValue(chatQuery, (snapshot) => {
      const now = Date.now();
      const items = [];

      snapshot.forEach((child) => {
        const value = child.val() || {};
        const dateCreated = typeof value.dateCreated === 'number' ? value.dateCreated : 0;
        if (!dateCreated) return; // skip malformed

        // Local safety filter: keep only last 24h
        if (now - dateCreated > ONE_DAY_MS) return;

        items.push({
          id: child.key,
          authorName: value.authorName || value.name || 'Unknown',
          userKey: value.userKey || null,
          text: value.text || '',
          dateCreated,
        });
      });

      items.sort((a, b) => a.dateCreated - b.dateCreated);

      setMessages(items);
      setHasLoaded(true);

      // Scroll to bottom on new messages
      if (listRef.current) {
        requestAnimationFrame(() => {
          const el = listRef.current;
          el.scrollTop = el.scrollHeight;
        });
      }
    });

    return () => unsubscribe();
  }, [nodeKey]);

  // ----- HANDLERS ----- //

  /**
   * handleSubmit
   *
   * Submits the current form.
   *
   * @param {object} event - event.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!nodeKey || !user) return;

    const text = inputValue.trim();
    if (!text) return;

    try {
      setIsSending(true);
      const now = Date.now();
      const expiresAt = now + ONE_DAY_MS;

      const chatRef = ref(realTimeDb, `streamChats/${nodeKey}`);
      await push(chatRef, {
        text,
        userKey: user.key,
        authorName: displayName,
        dateCreated: now,
        expiresAt,
      });

      setInputValue('');
    } catch (err) {
      console.error('StreamChat: failed to send message', err);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * handleInputChange
   *
   * Handles input change.
   *
   * @param {object} event - event.
   */
  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const isInputDisabled = !user || !nodeKey || isSending;

  // ----- RENDER ----- //

  return (
    <div className="stream-chat-container">

      {/* HEADER */}
      <div className="stream-chat-header">Live chat</div>

      {/* MESSAGES */}
      <div className="stream-chat-messages" ref={listRef}>

        {/* PLACEHOLDER */}
        {!hasLoaded && (

          <div className="stream-chat-placeholder">Loading chat…</div>
        )}

        {/* PLACEHOLDER */}
        {hasLoaded && messages.length === 0 && (

          <div className="stream-chat-placeholder">No messages yet. Be the first to say hi.</div>
        )}

        {messages.map((message) => (

          <div className="stream-chat-message" key={message.id}>

            {/* MESSAGE AUTHOR */}
            <span
              className="stream-chat-message-author"
            >
              {message.authorName}
            </span>

            {/* MESSAGE TEXT */}
            <span
              className="stream-chat-message-text"
            >
              {message.text}
            </span>
          </div>
        ))}
      </div>

      {/* FORM */}
      <form className="stream-chat-form" onSubmit={handleSubmit}>

        {/* INPUT */}
        <input
          className="stream-chat-input"
          type="text"
          placeholder={user ? 'Say something…' : 'Sign in to chat'}
          value={inputValue}
          disabled={isInputDisabled}
          maxLength={500}
          onChange={handleInputChange}
        />
        <Button
          label="Send"
          enabled={!isInputDisabled || inputValue.trim().length === 0}
          height="38px"
          borderRadius="9px"
          onClick={handleSubmit}
        />
      </form>
    </div>
  );
};

// Dev Tools
StreamChat.displayName = 'StreamChat';

export default StreamChat;
